import { CloseOutlined } from '@ant-design/icons'
import { Button, Form, Input, Modal, Select } from 'antd'
import { useForm } from 'antd/es/form/Form'
import dayjs from 'dayjs'
import { FC, useState } from 'react'
import useErrorModal from '../../../../../hooks/api/use-error-modal'
import { userStorage } from '../../../../../services/user-storage'
import { useUpdateRewindJobExecutorMutation } from '../../../../../store/api/rewind-job-executors-api'
import { useUpdateRewindJobMutation } from '../../../../../store/api/rewind-jobs-api'
import { rewindQueueEntriesApi } from '../../../../../store/api/rewind-queue-entries-api'
import { useAppDispatch } from '../../../../../store/store'
import { CancelationReasons } from '../../../../../types/api/print-job'
import { Reasons } from '../../../../../types/api/print-job-executor'
import { RewindJobStatuses } from '../../../../../types/api/rewind-job'
import RewindQueueEntry from '../../../../../types/api/rewind-queue-entry'
import {
    getCurrentUserActiveExecutor,
    isCurrentUserActiveExecutor,
} from '../helpers/rewind-job-helpers'

interface FormValues {
    cancelationReason: CancelationReasons
    otherCancelationReason?: string
}

interface CancelRewindProps {
    rewindQueueEntry: RewindQueueEntry
}

const CancelRewind: FC<CancelRewindProps> = ({ rewindQueueEntry }) => {
    const [open, setOpen] = useState<boolean>(false)
    const [form] = useForm<FormValues>()
    const [updateRewindJob, updateRewindJobState] = useUpdateRewindJobMutation()
    const [updateRewindJobExecutor, updateRewindJobExecutorState] =
        useUpdateRewindJobExecutorMutation()
    const rewindJob = rewindQueueEntry.rewind_job
    const currentUserDocumentId = userStorage.get()?.documentId
    const disabled = !isCurrentUserActiveExecutor(rewindJob, currentUserDocumentId)
    const isLoadingAnyRequest =
        updateRewindJobExecutorState.isLoading || updateRewindJobState.isLoading

    const dispatch = useAppDispatch()

    const onUpdateRewindJobExecutor = async () => {
        const executor = getCurrentUserActiveExecutor(rewindJob, currentUserDocumentId)

        if (!executor) return

        return updateRewindJobExecutor({
            docId: executor.documentId,
            body: {
                data: {
                    end: dayjs().toISOString(),
                    reason: Reasons.Cancel,
                },
            },
        })
    }

    const onConfirm = async () => {
        try {
            await form.validateFields()
            const values = form.getFieldsValue()
            const result = await onUpdateRewindJobExecutor()

            if (result && result.data && rewindJob?.documentId) {
                const updateResult = await updateRewindJob({
                    docId: rewindJob.documentId,
                    body: {
                        data: {
                            jobStatus: RewindJobStatuses.Cancelled,
                            cancelationReason: values.cancelationReason,
                            otherCancelationReason:
                                values.cancelationReason === CancelationReasons.Other
                                    ? values.otherCancelationReason
                                    : undefined,
                        },
                    },
                })

                if (updateResult.data) {
                    dispatch(rewindQueueEntriesApi.util.invalidateTags(['RewindQueueEntries']))
                    setOpen(false)
                }
            }
        } catch (e) {
            console.error(e)
        }
    }

    const onCancel = () => {
        setOpen(true)
    }

    useErrorModal(updateRewindJobExecutorState, 'Ошибка обновления исполнителя задания')
    useErrorModal(updateRewindJobState, 'Ошибка обновления задания перемотки')

    return (
        <>
            <Button icon={<CloseOutlined />} onClick={onCancel} disabled={disabled} danger>
                Отменить
            </Button>
            <Modal
                title='Отмена перемотки'
                open={open}
                onOk={onConfirm}
                confirmLoading={isLoadingAnyRequest}
                cancelButtonProps={{ disabled: isLoadingAnyRequest }}
                onCancel={() => setOpen(false)}
            >
                <Form form={form}>
                    <Form.Item
                        name='cancelationReason'
                        label='Причина отмены'
                        rules={[{ required: true }]}
                    >
                        <Select options={[{ value: CancelationReasons.Other }]} />
                    </Form.Item>
                    <Form.Item
                        shouldUpdate={(prev: FormValues, next: FormValues) =>
                            prev.cancelationReason !== next.cancelationReason
                        }
                    >
                        {form => {
                            const { cancelationReason } = form.getFieldsValue()

                            if (cancelationReason === CancelationReasons.Other) {
                                return (
                                    <Form.Item
                                        name='otherCancelationReason'
                                        label='Опишите причину'
                                        rules={[{ required: true }]}
                                    >
                                        <Input.TextArea />
                                    </Form.Item>
                                )
                            }

                            return null
                        }}
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
}

export default CancelRewind
